import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import {
  searchParams,
  statusParams,
  type ObjectStatus,
  type UsageParam,
} from '@/services/core/listSearch'
import { fetchAllPages, sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/** A reference to an object the ad unit uses, as the API names it. */
export interface AdUnitRef {
  id: string
  name: string
}

/**
 * Which line items an ad unit lets compete. By default untargeted ones do;
 * `ONLY_TARGETING` asks for some targeting to match, and
 * `ONLY_SELECTED_TARGETING` for targeting of the allowed types.
 */
export type AdUnitTargetingControl = 'NONE' | 'ONLY_TARGETING' | 'ONLY_SELECTED_TARGETING'

export interface AdUnit {
  id: string
  tagId: string
  name: string
  objectState: string
  labels: string[]
  /** The labels ad unit matching label targeting matches on. */
  matchingLabels: string[]
  paywalled: boolean
  dimensions: [number, number][]
  minWidth: number | null
  width: number | null
  minHeight: number | null
  height: number | null
  site: { id: string; name: string } | null
  earningsAccountId: string
  earningsAccountName: string
  requestedInLastDay: boolean
  filterTeamsType: string
  teams: { id: string; name: string }[]
  blocklist: AdUnitRef | null
  /** The inventory (floor price) ruleset. */
  siteRuleset: AdUnitRef | null
  targetingRulesets: AdUnitRef[]
  sizeRulesets: AdUnitRef[]
  floorPrice: { amount: number; currency: string } | null
  isFullPage: boolean
  /** The page path is matched as a category. */
  urlAsCategory: boolean
  /** The page's query string is read as key-values. */
  qsAsKeyValues: boolean
  targetingControl: AdUnitTargetingControl
  /** The targeting types that count under `ONLY_SELECTED_TARGETING`. */
  allowedTargetTypes: string[]
  /** Overrides the site's political advertising policy when set. */
  politicalAdvertising: string
  createTime: string
  updateTime: string
  /** Problems the server found on the last save; shown on the edit page. */
  validationWarnings: ValidationWarning[]
}

export interface AdUnitInput {
  name: string
  paywalled?: boolean
  labels?: string[]
  matchingLabels?: string[]
  dimensions?: [number, number][]
  minWidth?: number | null
  width?: number | null
  minHeight?: number | null
  height?: number | null
  objectState?: string
  site?: { id: string }
}

function mapRef(r: unknown): AdUnitRef | null {
  if (typeof r !== 'object' || r === null) return null
  const ref = r as Record<string, unknown>
  return ref.id ? { id: ref.id as string, name: (ref.name as string) ?? '' } : null
}

const mapRefs = (r: unknown): AdUnitRef[] =>
  Array.isArray(r) ? r.map(mapRef).filter((ref): ref is AdUnitRef => !!ref) : []

function mapAdUnit(r: Record<string, unknown>): AdUnit {
  const teams = (r.teams as Record<string, unknown>[]) ?? []
  const siteRaw =
    typeof r.site === 'object' && r.site !== null ? (r.site as Record<string, unknown>) : undefined
  const site = (siteRaw?.id as string) ? siteRaw : undefined
  const ea = (r.earningsAccount ?? siteRaw?.earningsAccount) as Record<string, unknown> | undefined
  return {
    id: (r.id as string) ?? '',
    tagId: (r.tagId as string) ?? '',
    name: (r.name as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    labels: (r.labels as string[]) ?? [],
    matchingLabels: (r.matchingLabels as string[]) ?? [],
    paywalled: (r.paywalled as boolean) ?? false,
    dimensions: (r.dimensions as [number, number][]) ?? [],
    minWidth: (r.minWidth as number) ?? null,
    width: (r.width as number) ?? null,
    minHeight: (r.minHeight as number) ?? null,
    height: (r.height as number) ?? null,
    site: site ? { id: (site.id as string) ?? '', name: (site.name as string) ?? '' } : null,
    earningsAccountId: (ea?.id as string) ?? '',
    earningsAccountName: (ea?.name as string) ?? '',
    requestedInLastDay: (r.requestedInLastDay as boolean) ?? false,
    filterTeamsType: (r.filterTeamsType as string) ?? '',
    teams: teams.map((t) => ({ id: (t.id as string) ?? '', name: (t.name as string) ?? '' })),
    blocklist: mapRef(r.blocklist),
    siteRuleset: mapRef(r.siteRuleset),
    targetingRulesets: mapRefs(r.targetingRulesets),
    sizeRulesets: mapRefs(r.sizeRulesets),
    floorPrice: (r.floorPrice as AdUnit['floorPrice']) ?? null,
    isFullPage: (r.isFullPage as boolean) ?? false,
    urlAsCategory: (r.urlAsCategory as boolean) ?? false,
    qsAsKeyValues: (r.qsAsKeyValues as boolean) ?? false,
    targetingControl: (r.targetingControl as AdUnitTargetingControl) ?? 'NONE',
    allowedTargetTypes: (r.allowedTargetTypes as string[]) ?? [],
    politicalAdvertising: (r.politicalAdvertising as string) ?? '',
    createTime: (r.createTime as string) ?? '',
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    updateTime: (r.updateTime as string) ?? '',
  }
}

/** A sorted page of the active ad units. */
async function fetchActivePage(
  orderBy: string,
  orderByDirection: 'ASCENDING' | 'DESCENDING',
  page: number,
  pageSize: number
): Promise<{ results: AdUnit[]; totalCount: number }> {
  const params = new URLSearchParams({
    concise: 'true',
    context: getContext(),
    includeActive: 'true',
    includeHidden: 'false',
    includeInactive: 'false',
    orderBy,
    orderByDirection,
    // A never-requested ad unit has no flag at all; it belongs with the false ones.
    nullsOrderPolicy: 'FIRST',
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/adunits?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch ad units')
  const data = await res.json()
  const results = toArray(data).map(mapAdUnit)
  return { results, totalCount: data.totalCount ?? results.length }
}

/** The options a ad unit list page is narrowed by on the server. */
export interface AdUnitListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  /** Publisher (earnings account) id to narrow to. */
  earningsAccountId?: string
  /** Site id to narrow to. */
  siteId?: string
  sort?: ListSort | null
}

type AdUnitFilters = Pick<AdUnitListOptions, 'search' | 'status' | 'earningsAccountId' | 'siteId'>

/**
 * The API reads one owner parameter and ignores the rest — with both set,
 * `earningsAccount` wins — so a site, being the narrower, goes alone.
 */
function ownerParams({ earningsAccountId, siteId }: AdUnitFilters): Record<string, string> {
  if (siteId) return { site: siteId }
  if (earningsAccountId) return { earningsAccount: earningsAccountId }
  return {}
}

/** One page of ad units, narrowed by the given filters on the server. */
async function fetchAdUnitPage(
  filters: AdUnitFilters,
  page: number,
  pageSize: number,
  sort?: ListSort | null
): Promise<ListPage<AdUnit>> {
  const { search, status } = filters
  const params = new URLSearchParams({
    ...searchParams('adUnits', search),
    ...ownerParams(filters),
    concise: 'true',
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/adunits?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch ad units')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapAdUnit)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const AdUnitsApi = {
  /** One page of ad units, filtered on the server. */
  getPage: ({ page = 1, pageSize = 25, sort, ...filters }: AdUnitListOptions = {}) =>
    fetchAdUnitPage(filters, page, pageSize, sort),

  /** Every ad unit matching the filters, walked page by page — for downloads. */
  getAllPages: (filters: AdUnitFilters = {}): Promise<AdUnit[]> =>
    fetchAllPages((page, pageSize) => fetchAdUnitPage(filters, page, pageSize)),

  /** The newest created, or most recently changed, active ad units. */
  getRecent: async (sort: 'createTime' | 'updateTime', limit: number): Promise<AdUnit[]> =>
    (await fetchActivePage(sort, 'DESCENDING', 1, limit)).results,

  /**
   * Active ad units that have not been requested in the last day. The API
   * cannot filter on the flag, so the list is sorted on it — unrequested first
   * — and read until the requested ones start, or `maxPages` run out.
   */
  getUnrequested: async (maxPages = 5): Promise<{ results: AdUnit[]; truncated: boolean }> => {
    const results: AdUnit[] = []
    for (let page = 1; page <= maxPages; page++) {
      const next = await fetchActivePage('requestedInLastDay', 'ASCENDING', page, 100)
      const unrequested = next.results.filter((adUnit) => !adUnit.requestedInLastDay)
      results.push(...unrequested)
      if (unrequested.length < next.results.length || next.results.length < 100) {
        return { results, truncated: false }
      }
    }
    // Every page read was unrequested, so there may be more past the cap.
    return { results, truncated: true }
  },

  getAll: async (search?: string, status?: ObjectStatus): Promise<AdUnit[]> => {
    const params = new URLSearchParams({
      ...searchParams('adUnits', search),
      concise: 'true',
      context: getContext(),
      includeHidden: 'false',
      ...statusParams(status),
      locale: 'no',
      orderBy: 'requestedInLastDay',
      orderByDirection: 'DESCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/adunits?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch ad units')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapAdUnit)
  },

  getBySite: async (siteId: string, search?: string, status?: ObjectStatus): Promise<AdUnit[]> => {
    const params = new URLSearchParams({
      ...searchParams('adUnits', search),
      concise: 'true',
      context: getContext(),
      site: siteId,
      includeHidden: 'false',
      ...statusParams(status),
      orderBy: 'requestedInLastDay',
      orderByDirection: 'DESCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/adunits?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch ad units')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapAdUnit)
  },

  getByEarningsAccount: async (
    earningsAccountId: string,
    search?: string,
    status?: ObjectStatus
  ): Promise<AdUnit[]> => {
    const params = new URLSearchParams({
      ...searchParams('adUnits', search),
      concise: 'true',
      context: getContext(),
      earningsAccount: earningsAccountId,
      includeHidden: 'false',
      ...statusParams(status),
      orderBy: 'requestedInLastDay',
      orderByDirection: 'DESCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/adunits?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch ad units')
    return toArray(await res.json()).map(mapAdUnit)
  },

  /** Everything using the given blocklist or ruleset. */
  getByUsage: async (usageParam: UsageParam, resourceId: string): Promise<AdUnit[]> => {
    const params = new URLSearchParams({
      concise: 'true',
      context: getContext(),
      [usageParam]: resourceId,
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'true',
      orderBy: 'requestedInLastDay',
      orderByDirection: 'DESCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/adunits?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch ad units')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapAdUnit)
  },

  /**
   * Fetches several ad units in one request. The API's `id` parameter is
   * semicolon-separated; ids the network does not hold are left out.
   */
  getByIds: async (ids: string[]): Promise<AdUnit[]> => {
    if (ids.length === 0) return []
    const params = new URLSearchParams({
      concise: 'true',
      context: getContext(),
      id: ids.join(';'),
      pageSize: String(ids.length),
    })
    const res = await apiFetch(`${API_BASE}/adunits?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch ad units')
    return toArray(await res.json()).map(mapAdUnit)
  },

  getById: async (id: string): Promise<AdUnit> => {
    const res = await apiFetch(`${API_BASE}/adunits/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch ad unit')
    return mapAdUnit(await res.json())
  },

  create: async (id: string, input: AdUnitInput): Promise<AdUnit> => {
    const body = { id, ...input }
    const res = await apiFetch(`${API_BASE}/adunits/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create ad unit')
    return mapAdUnit(await res.json())
  },

  update: async (id: string, input: AdUnitInput): Promise<AdUnit> => {
    const res = await apiFetch(`${API_BASE}/adunits/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save ad unit')
    return mapAdUnit(await res.json())
  },

  bulkSave: async (items: (AdUnitInput & { id: string })[]): Promise<AdUnit[]> => {
    const res = await apiFetch(`${API_BASE}/adunits?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(items),
    })
    if (!res.ok) await throwApiError(res, 'Failed to bulk save ad units')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapAdUnit)
  },
}
